/**
 * Phase 2 (Feb 2026) — Budget recommendation helper.
 *
 * Concept demands NO fixed default (5€/30€/etc.). Instead a derived
 * value based on:
 *   monthly_budget_eur = (target_leads_per_month
 *                          × cpl_estimate_eur_high)
 *                        ÷ margin_factor
 *   daily_budget_eur   = monthly_budget_eur / 30
 *
 * `cpl_estimate_eur_high` is the top of the industry CPL range for the
 * tenant's category (falls back to 30€ if unknown). `margin_factor` is
 * a headroom multiplier ≥1 (defaults to 1.2 for a 20% safety cushion).
 *
 * The full formula is exposed via `explain()` for the tooltip so the
 * user can see WHY the number is what it is (Korrektur 7 — no black-box).
 */

const DEFAULT_CPL_RANGE_EUR = { low: 15, high: 30 };

// Coarse category → CPL range lookup. Numbers based on industry
// benchmarks the operator can override per tenant later.
const CATEGORY_CPL_RANGES = {
  real_estate: { low: 18, high: 35 },
  ecommerce: { low: 8, high: 20 },
  b2b_services: { low: 30, high: 80 },
  local_services: { low: 10, high: 25 },
  saas: { low: 25, high: 60 },
};

export function deriveBudget({
  target_leads_per_month = 30,
  category = null,
  duration_days = 30,
  margin_factor = 1.2,
} = {}) {
  const range = CATEGORY_CPL_RANGES[category] || DEFAULT_CPL_RANGE_EUR;
  const monthly = Math.ceil(
    (target_leads_per_month * range.high) / Math.max(margin_factor, 1),
  );
  const daily = Math.ceil(monthly / 30);
  const total_for_duration = Math.ceil((monthly / 30) * duration_days);
  return {
    daily_eur: daily,
    monthly_eur: monthly,
    total_for_duration_eur: total_for_duration,
    cpl_range_eur: range,
    formula:
      `${target_leads_per_month} Anfragen/Monat × ${range.high} € CPL (obere Grenze) ` +
      `÷ ${margin_factor.toFixed(2)} = ${monthly} €/Monat ≈ ${daily} €/Tag`,
    inputs: {
      target_leads_per_month,
      cpl_high: range.high,
      cpl_low: range.low,
      duration_days,
      margin_factor,
      category: category || "generic",
    },
  };
}

/** Human-readable explanation for the Onboarding tooltip. */
export function explainBudget(res) {
  const { formula, inputs } = res;
  return [
    `Empfehlung basiert auf deinen Angaben:`,
    `• Ziel-Anfragen: ${inputs.target_leads_per_month} pro Monat`,
    `• CPL-Spanne (${inputs.category}): ${inputs.cpl_low} € – ${inputs.cpl_high} €`,
    `• Sicherheitszuschlag: ×${inputs.margin_factor}`,
    `Rechnung: ${formula}`,
    `Du kannst den Betrag jederzeit anpassen — VIONA warnt, wenn er zu knapp wird.`,
  ].join("\n");
}
